// Pantalla del rol admin (Sprint 6 T9) — reportes de auditoria.
// 3 tabs: Decisiones jefes F11 / Alergias por fuente / Sync Odoo.
// Cada tab ofrece filtros (sede_id, periodo), una tabla y un boton para
// descargar el CSV con los mismos filtros aplicados.
import { useEffect } from 'react'
import AppHeader from '../components/AppHeader'
import { useAuth } from '../hooks/useAuth'
import {
  useReportesAdminStore,
  type ReportesTab,
} from '../stores/reportesAdmin'
import type { ExportKind } from '../services/reportesAdmin'

const TABS: { key: ReportesTab; label: string; kind: ExportKind }[] = [
  { key: 'decisiones', label: 'Decisiones F11', kind: 'decisiones-jefes' },
  { key: 'alergias', label: 'Alergias por fuente', kind: 'alergias-fuente' },
  { key: 'sync', label: 'Sync Odoo', kind: 'sync-odoo' },
]

export default function AdminReportes() {
  const { user, logout } = useAuth()
  const {
    tab,
    filtros,
    decisiones,
    alergias,
    sync,
    loading,
    error,
    setTab,
    setFiltro,
    refrescarTabActiva,
    exportar,
  } = useReportesAdminStore()

  useEffect(() => {
    refrescarTabActiva()
  }, [tab, refrescarTabActiva])

  const tabActual = TABS.find((t) => t.key === tab) ?? TABS[0]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-50">
      <AppHeader title="Admin — Reportes de auditoria" user={user} onLogout={logout} />

      <div
        role="tablist"
        aria-label="Tipos de reporte"
        className="flex border-b bg-white dark:bg-gray-800 sticky top-[64px] z-10"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            aria-controls={`panel-${t.key}`}
            id={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 ${
              tab === t.key
                ? 'border-avante-primary text-avante-primary'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <main className="p-4 space-y-4 max-w-6xl mx-auto">
        <section
          aria-label="Filtros del reporte"
          className="flex flex-wrap gap-3 items-end bg-white dark:bg-gray-800 p-3 rounded shadow"
        >
          {tab !== 'sync' && (
            <label className="block">
              <span className="text-xs font-semibold">Sede ID</span>
              <input
                type="number"
                value={filtros.sede_id ?? ''}
                onChange={(e) => {
                  const v = e.target.value
                  setFiltro('sede_id', v === '' ? null : Number(v))
                }}
                placeholder="todas"
                aria-label="Filtrar por sede"
                className="mt-1 w-24 border rounded px-2 py-1 text-sm"
              />
            </label>
          )}
          <label className="block">
            <span className="text-xs font-semibold">Fecha inicio</span>
            <input
              type="date"
              value={filtros.fecha_inicio ?? ''}
              onChange={(e) => setFiltro('fecha_inicio', e.target.value || null)}
              aria-label="Fecha inicio del periodo"
              className="mt-1 border rounded px-2 py-1 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold">Fecha fin</span>
            <input
              type="date"
              value={filtros.fecha_fin ?? ''}
              onChange={(e) => setFiltro('fecha_fin', e.target.value || null)}
              aria-label="Fecha fin del periodo"
              className="mt-1 border rounded px-2 py-1 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={() => refrescarTabActiva()}
            className="bg-avante-primary text-white px-3 py-2 rounded text-sm font-semibold"
          >
            Aplicar filtros
          </button>
          <button
            type="button"
            onClick={() => exportar(tabActual.kind)}
            className="bg-gray-700 text-white px-3 py-2 rounded text-sm font-semibold"
            aria-label="Exportar reporte a CSV"
          >
            Exportar CSV
          </button>
        </section>

        {loading && <p className="text-sm text-gray-500">Cargando…</p>}
        {error && (
          <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </p>
        )}

        {tab === 'decisiones' && (
          <section
            id="panel-decisiones"
            role="tabpanel"
            aria-labelledby="tab-decisiones"
          >
            {decisiones.length === 0 && !loading && (
              <p className="text-sm text-gray-500">Sin datos para el periodo seleccionado.</p>
            )}
            <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded shadow">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 dark:bg-gray-700 text-left">
                  <tr>
                    <th className="p-2">Sede</th>
                    <th className="p-2">Aprobadas</th>
                    <th className="p-2">Rechazadas</th>
                    <th className="p-2">Expiradas</th>
                    <th className="p-2">Total</th>
                    <th className="p-2">Tiempo resp. (min)</th>
                    <th className="p-2">Ratio aprob.</th>
                  </tr>
                </thead>
                <tbody>
                  {decisiones.map((r) => (
                    <tr key={r.sede_id} className="border-t">
                      <td className="p-2 font-mono text-xs">{r.sede_codigo} — {r.sede_nombre}</td>
                      <td className="p-2">{r.aprobadas}</td>
                      <td className="p-2">{r.rechazadas}</td>
                      <td className="p-2">{r.expiradas}</td>
                      <td className="p-2">{r.total}</td>
                      <td className="p-2">{r.tiempo_respuesta_promedio_min ?? '—'}</td>
                      <td className="p-2">
                        {r.ratio_aprobacion != null
                          ? `${(r.ratio_aprobacion * 100).toFixed(1)}%`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === 'alergias' && (
          <section
            id="panel-alergias"
            role="tabpanel"
            aria-labelledby="tab-alergias"
          >
            {alergias.length === 0 && !loading && (
              <p className="text-sm text-gray-500">Sin alergias en el periodo seleccionado.</p>
            )}
            <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded shadow">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 dark:bg-gray-700 text-left">
                  <tr>
                    <th className="p-2">Fuente</th>
                    <th className="p-2">Total</th>
                    <th className="p-2">Validadas clinicamente</th>
                    <th className="p-2">Contradichas</th>
                    <th className="p-2">Activas</th>
                  </tr>
                </thead>
                <tbody>
                  {alergias.map((r) => (
                    <tr key={r.fuente} className="border-t">
                      <td className="p-2 font-mono text-xs">{r.fuente}</td>
                      <td className="p-2">{r.total}</td>
                      <td className="p-2">{r.validadas_clinicamente}</td>
                      <td className="p-2">{r.contradichas}</td>
                      <td className="p-2">{r.activas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === 'sync' && (
          <section
            id="panel-sync"
            role="tabpanel"
            aria-labelledby="tab-sync"
          >
            {sync.length === 0 && !loading && (
              <p className="text-sm text-gray-500">Sin eventos sync en el periodo seleccionado.</p>
            )}
            <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded shadow">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 dark:bg-gray-700 text-left">
                  <tr>
                    <th className="p-2">Entidad</th>
                    <th className="p-2">Estado</th>
                    <th className="p-2">Total</th>
                    <th className="p-2">Ultimo evento</th>
                  </tr>
                </thead>
                <tbody>
                  {sync.map((r) => (
                    <tr key={`${r.entidad_tipo}-${r.estado}`} className="border-t">
                      <td className="p-2 font-mono text-xs">{r.entidad_tipo}</td>
                      <td className="p-2">{r.estado}</td>
                      <td className="p-2">{r.total}</td>
                      <td className="p-2 text-xs">
                        {r.ultimo_evento
                          ? new Date(r.ultimo_evento).toLocaleString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
